import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useCardStatusStore } from "../stores/cardStatusStore";
import {
  getCreditDelegationTxData,
  linkCardToCreditAccount,
  createCard,
  getUserCards,
} from "../api/api";
import { dataFetcher } from "./dataFetcher";

export interface DelegateCardParams {
  creditType: "aave-v3" | "euler";
  creditLimit: number;
  sendTransactionAsync: (params: {
    to: string;
    value: bigint;
    data: string;
  }) => Promise<string>; // Returns transaction hash
  signTypedDataAsync: (params: {
    domain: { chainId: number };
    types: Record<string, Array<{ name: string; type: string }>>;
    primaryType: string;
    message: Record<string, any>;
  }) => Promise<string>; // Returns signature
}

/**
 * Complete card delegation process
 * Handles delegation transaction, signing, and linking in one flow
 */
export async function delegateCard(params: DelegateCardParams): Promise<void> {
  const { eoaAddress } = useSDKConfigStore.getState();

  if (!eoaAddress) {
    throw new Error("EOA address not found. Please initialize the SDK first.");
  }

  if (params.creditLimit <= 0) {
    throw new Error("Credit limit must be greater than 0.");
  }

  try {
    // Step 1: Get credit delegation transaction data
    const delegationResponse = await getCreditDelegationTxData({
      from: eoaAddress,
      creditLimit: params.creditLimit,
      creditType: params.creditType,
    });

    console.log({ delegationResponse });
    const delegation = delegationResponse.data;
    console.log({ delegation });

    if (!delegation.to || !delegation.callData) {
      throw new Error(
        "Invalid transaction data received from delegation service"
      );
    }

    // Step 2: Execute transaction
    const transactionHash = await params.sendTransactionAsync({
      to: delegation.to,
      value: BigInt(delegation.value || 0),
      data: delegation.callData,
    });
    console.log({ transactionHash });

    // Step 2.5: Create card after transaction is executed
    const createCardResponse = await createCard(eoaAddress);

    console.log({ createCardResponse });

    const createdCard = createCardResponse.data;

    if (!createdCard.id) {
      throw new Error("Failed to create card - invalid card ID received");
    }

    // Step 3: Generate signature using created card details
    const signature = await params.signTypedDataAsync({
      domain: {
        chainId: 8453,
      },
      types: {
        DelegationParams: [
          { name: "cardID", type: "string" },
          { name: "creditProvider", type: "address" },
          { name: "creditType", type: "string" },
        ],
      },
      primaryType: "DelegationParams",
      message: {
        cardID: createdCard.id,
        creditProvider: eoaAddress,
        creditType: params.creditType,
      },
    });

    console.log({ signature });

    // Step 4: Link card to credit account using created card details
    await linkCardToCreditAccount({
      cardID: createdCard.id,
      creditProviderAddress: eoaAddress,
      chainID: 8453, // Base chain ID
      signature,
      creditType: params.creditType,
    });

    // Step 5: Verify delegation by checking holding address
    const userCardsResponse = await getUserCards(eoaAddress);

    console.log({ userCardsResponse });

    const updatedCard = userCardsResponse.data?.find(
      (card) => card.cardID === createdCard.id
    );

    if (!updatedCard) {
      throw new Error(
        "Card not found after delegation - delegation may have failed"
      );
    }

    if (updatedCard.holdingAddress.toLowerCase() !== eoaAddress.toLowerCase()) {
      throw new Error(
        "Delegation failed - holding address does not match EOA address"
      );
    }

    // Step 6: Refetch all data to update the store with latest status
    await dataFetcher.fetchDataAfterAuth();
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "Failed to delegate card";
    throw new Error(errorMessage);
  }
}
